import React, { useState } from 'react';
import { File as FileIcon, Download } from 'lucide-react';
import { getAbsoluteUrl } from '../../api/client';

interface AttachmentProps {
    attachments: any[];
    authToken?: string | null;
}

export const MessageAttachments = ({ attachments, authToken }: AttachmentProps) => {
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

    if (!attachments || attachments.length === 0) return null;

    const handleDownload = async (e: React.MouseEvent, url: string, filename: string) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Failed to download file programmatically:', error);
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <>
            <div className="message-attachments" style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {attachments.map((att: any, i: number) => {
                    const fileName = att.original_name || att.name || 'File';
                    const fileSize = att.size_bytes || att.size || 0;
                    const isImage = att.mime_type?.startsWith('image/') || att.type?.startsWith('image/');

                    let fileUrl = (authToken && (att.url.includes('/api/files/') || att.url.startsWith('/api/')))
                        ? `${att.url}${att.url.includes('?') ? '&' : '?'}token=${encodeURIComponent(authToken)}`
                        : att.url;

                    fileUrl = getAbsoluteUrl(fileUrl);

                    return isImage ? (
                        <div key={i} className="message-attachment-thumbnail">
                            <img
                                src={fileUrl}
                                alt={fileName}
                                onClick={() => setLightboxUrl(fileUrl)}
                                title="Click to enlarge"
                            />
                            <a
                                href={fileUrl}
                                download={fileName}
                                style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.55)', borderRadius: 4, padding: '2px 4px', display: 'flex', alignItems: 'center', color: 'white' }}
                                title="Download"
                                onClick={e => handleDownload(e, fileUrl, fileName)}
                            >
                                <Download size={12} />
                            </a>
                        </div>
                    ) : (
                        <a key={i} href={fileUrl} onClick={(e) => handleDownload(e, fileUrl, fileName)} rel="noopener noreferrer" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            background: 'var(--bg-secondary)',
                            padding: '8px 12px',
                            borderRadius: 6,
                            textDecoration: 'none',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-color)',
                            fontSize: 13
                        }}>
                            <FileIcon size={16} />
                            <span>{fileName}</span>
                            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                                ({fileSize > 0 ? `${Math.round(fileSize / 1024)}KB` : 'Size unknown'})
                            </span>
                            <Download size={14} style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} />
                        </a>
                    );
                })}
            </div>

            {/* Lightbox */}
            {lightboxUrl && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.85)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        cursor: 'zoom-out'
                    }}
                    onClick={() => setLightboxUrl(null)}
                >
                    <img 
                        src={lightboxUrl} 
                        style={{ maxWidth: '90%', maxHeight: '90%', boxShadow: '0 0 40px rgba(0,0,0,0.5)' }} 
                        alt="Enlarged view"
                    />
                </div>
            )}
        </>
    );
};
